// SearchPanel.jsx
// -------------------------------------------------------------
// Professional UX/UI redesign for desktop search panel
// Enhanced visual hierarchy, consistent sizing, and modern design patterns
// -------------------------------------------------------------
import React, { useMemo } from "react";
import { Box, Typography, Tooltip, Divider } from "@mui/material";
import { Close as CloseIcon, KeyboardArrowLeft, KeyboardArrowRight, Search as SearchIcon } from "@mui/icons-material";
import SearchPanelCarousel from "./SearchPanelCarousel";
import CustomScrollbar from './CustomScrollbar';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { createTranslator, getBrowserLanguage } from "../utils/i18n";

/**
 * Professional search panel with enhanced UX/UI design
 * Consistent sizing with chat, improved visual hierarchy, and modern aesthetics
 */
const SearchPanelWrapper = styled(motion.div)(({ theme }) => ({
  width: "min(720px, 48vw)", // slightly wider for carousel comfort
  height: "100%", // Match chat height exactly
  backgroundColor: "#ffffff",
  borderRadius: "20px",
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  position: "relative",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  // Enhanced visual connection with chat
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-8px',
    top: '20%',
    bottom: '20%',
    width: '4px',
    background: 'linear-gradient(to bottom, transparent, #2196f3, transparent)',
    borderRadius: '2px',
    opacity: 0.6,
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    borderRadius: 0,
    boxShadow: 'none',
  }
}));

// Professional header design
const SearchPanelHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  padding: '20px 24px 16px',
  position: 'relative',
  zIndex: 10,
}));

// Modern close button with better UX
const CloseButton = styled(motion.button)(({ theme }) => ({
  position: 'absolute',
  top: '16px',
  right: '16px',
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
  '& svg': {
    fontSize: '18px',
    color: 'rgba(0, 0, 0, 0.6)',
  }
}));

// Enhanced navigation controls
const NavigationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  marginTop: '12px',
}));

const NavigationButton = styled(motion.button)(({ theme }) => ({
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  padding: 0,
  backgroundColor: '#ffffff',
  color: '#64748b',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.2s ease',
  '&:hover:not(:disabled)': {
    backgroundColor: '#f8fafc',
    borderColor: '#2196f3',
    color: '#2196f3',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)',
  },
  '&:active:not(:disabled)': {
    transform: 'translateY(0)',
  },
  '&:disabled': {
    backgroundColor: '#f8fafc',
    color: 'rgba(0, 0, 0, 0.26)',
    cursor: 'not-allowed',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    boxShadow: 'none',
  },
  '& svg': {
    fontSize: '20px',
  },
}));

// Professional result counter with enhanced design
const ResultCounter = styled(motion.div)(({ theme }) => ({
  backgroundColor: '#2196f3',
  color: 'white',
  padding: '12px 20px',
  borderRadius: '16px',
  fontWeight: 600,
  fontSize: '0.95rem',
  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.25)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '200px',
  textAlign: 'center',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.2), transparent)',
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'xor',
  }
}));

// Enhanced content area
const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: '#fafbfc',
}));

// Professional empty state
const EmptyState = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  padding: '40px 24px',
  textAlign: 'center',
  color: '#64748b',
}));

const EmptyStateIcon = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '20px',
  backgroundColor: 'rgba(33, 150, 243, 0.08)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '24px',
  '& svg': {
    fontSize: '36px',
    color: '#2196f3',
    opacity: 0.7,
  }
}));

// Enhanced vehicle card container
const VehicleCardContainer = styled(motion.div)(({ theme }) => ({
  backgroundColor: '#ffffff',
  margin: '16px',
  padding: '20px',
  borderRadius: '16px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
  border: '1px solid rgba(0, 0, 0, 0.04)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    borderColor: 'rgba(33, 150, 243, 0.2)',
  },
}));

const SearchPanel = ({
  onClose,
  searchMessages = [],
  onNavigateSearch,
  currentIndex,
  totalCount,
  isLoading,
}) => {
  const language = useMemo(() => getBrowserLanguage(), []);
  const t = useMemo(() => createTranslator(language), [language]);
  const hasMultiple = totalCount > 1;

  // Filter messages to only show those with car carousel items
  const carouselMessages = searchMessages.filter(
    msg => msg.carouselItems && Array.isArray(msg.carouselItems) && msg.carouselItems.length > 0
  );

  // Get the current message being displayed
  const currentMessage = carouselMessages[currentIndex] || null;
  
  // Calculate total vehicles across all pages
  const totalVehicles = carouselMessages.reduce((total, msg) => {
    return total + (msg.carouselItems?.length || 0);
  }, 0);

  return (
    <AnimatePresence>
      <SearchPanelWrapper
        initial={{ opacity: 0, x: 50, rotateY: -15 }}
        animate={{ 
          opacity: 1, 
          x: 0, 
          rotateY: 0,
          transition: { 
            type: "spring", 
            stiffness: 300, 
            damping: 30,
            duration: 0.6 
          }
        }}
        exit={{ 
          opacity: 0, 
          x: 50, 
          rotateY: -15,
          transition: { duration: 0.4 }
        }}
      >
        {/* Professional Header */}
        <SearchPanelHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '10px',
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SearchIcon sx={{ color: '#2196f3', fontSize: 20 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#1e293b',
                  lineHeight: 1.2,
                  mb: 0.5
                }}
              >
                {t("searchResultsTitle")}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: '0.85rem'
                }}
              >
                {t("searchResultsSubtitle")}
              </Typography>
            </Box>
          </Box>

          {/* Enhanced Navigation and Counter */}
          {totalCount > 0 && (
            <NavigationContainer>
              {hasMultiple && (
                <Tooltip title={t("previousResult")} placement="bottom">
                  <NavigationButton
                    onClick={() => onNavigateSearch(-1)}
                    disabled={currentIndex <= 0}
                    aria-label="Previous result"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <KeyboardArrowLeft />
                  </NavigationButton>
                </Tooltip>
              )}
              
              <ResultCounter
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                {hasMultiple
                  ? `${currentIndex + 1}/${totalCount}`
                  : `${totalCount} ${totalCount === 1 ? t("resultsCounterSingular") : t("resultsCounterPlural")}`} • {totalVehicles} {totalVehicles === 1 ? t("vehiclesCounterSingular") : t("vehiclesCounterPlural")}
              </ResultCounter>
              
              {hasMultiple && (
                <Tooltip title={t("nextResult")} placement="bottom">
                  <NavigationButton
                    onClick={() => onNavigateSearch(1)}
                    disabled={currentIndex >= totalCount - 1}
                    aria-label="Next result"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <KeyboardArrowRight />
                  </NavigationButton>
                </Tooltip>
              )}
            </NavigationContainer>
          )}

          {/* Modern Close Button */}
          <Tooltip title={t("closeSearchPanel")} placement="left">
            <CloseButton
              onClick={onClose}
              aria-label="close search panel"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <CloseIcon />
            </CloseButton>
          </Tooltip>
        </SearchPanelHeader>

        <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.04)' }} />

        {/* Enhanced Content Area */}
        <ContentArea>
          <CustomScrollbar>
            {carouselMessages.length === 0 ? (
              <EmptyState
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <EmptyStateIcon>
                  <SearchIcon />
                </EmptyStateIcon>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }}
                >
                  {t("noResultsTitle")}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#64748b',
                    maxWidth: '280px',
                    lineHeight: 1.5
                  }}
                >
                  {t("noResultsDescription")}
                </Typography>
              </EmptyState>
            ) : (
              <Box sx={{ py: 1 }}>
                {carouselMessages.map((msg, idx) => (
                  <VehicleCardContainer
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <SearchPanelCarousel items={msg.carouselItems} />
                  </VehicleCardContainer>
                ))}
              </Box>
            )}
          </CustomScrollbar>
        </ContentArea>
      </SearchPanelWrapper>
    </AnimatePresence>
  );
};

export default SearchPanel;
